import {
  Constructor,
  type ILabel,
  IOrientedRectangle,
  type IRenderContext,
  LabelStyleBase,
  Rect,
  Size,
  SvgVisual,
  Visual,
} from '@ardoq/yfiles';
import { createSvgElement } from '@ardoq/dom-utils';
import {
  type GraphItem,
  LabelExportSupport,
  isAggregatedGraphEdge,
  isParentChildGraphEdge,
} from '@ardoq/graph';
import {
  visualDiffModes,
  LABEL_VERTICAL_PADDING,
  DATA_RENDER_HASH,
  LABEL_HORIZONTAL_PADDING,
  BLOCK_DIAGRAM_EDGE_LABEL_FONT,
  BLOCK_DIAGRAM_NODE_LABEL_FONT,
} from 'yfilesExtensions/styles/consts';
import { GLOBAL_HANDLER_ID_ATTRIBUTE } from 'consts';
import { classes } from '@ardoq/common-helpers';
import { DiffType } from '@ardoq/data-model';
import {
  createAndAppendLabels,
  getEdgeMarkersInset,
  getMultiLabelHash,
} from './labelUtils';
import { OTHER_LABEL_TEXT_ELEMENT_HORIZONTAL_PADDING } from './consts';
import { measureLabelElement } from 'yfilesExtensions/styles/measureLabels';
import {
  COMPONENT_ID_ATTRIBUTE,
  REFERENCE_ID_ATTRIBUTE,
} from '@ardoq/global-consts';
import { dataModelId } from 'tabview/graphComponent/graphComponentUtil';
import {
  LEGACY_FORMATTING_VALUE_HEIGHT_NODE,
  measureNodeLabelWidth,
} from './legacyLabelUtils';
import { colors } from '@ardoq/design-tokens';
import {
  type LabelStyleProperties,
  createMultiLabelExporter,
} from '@ardoq/export';
import { createLabelStyle } from '../shared/styleUtils';

type LabelMetaProperties = {
  modelId: string;
  isComponent: boolean;
  isVisualDiffMode: boolean;
  isPlaceholder: boolean;
  isReference: boolean;
  isParentChildReference: boolean;
  isGroup: boolean;
};
const getLabelMetaProperties = (graphItem: GraphItem): LabelMetaProperties => {
  const modelId = dataModelId(graphItem);
  const isComponent = graphItem.isComponent();
  const diffType = graphItem.getVisualDiffType();
  const isVisualDiffMode = visualDiffModes.has(diffType);
  const isPlaceholder = diffType === DiffType.PLACEHOLDER;
  const isReference =
    graphItem.isReference() || isAggregatedGraphEdge(graphItem);
  const isParentChildReference = isParentChildGraphEdge(graphItem);
  const isGroup = graphItem.isGroup();
  return {
    modelId,
    isComponent,
    isVisualDiffMode,
    isPlaceholder,
    isReference,
    isParentChildReference,
    isGroup,
  };
};

type GetLabelContainerArgs = {
  container: SVGGElement;
  layout: IOrientedRectangle;
  isParentChildReference: boolean;
  modelId: string;
  isComponent: boolean;
  isReference: boolean;
  isGroup: boolean;
  isVisualDiffMode: boolean;
  isPlaceholder: boolean;
};
const enhanceLabelContainer = ({
  container,
  layout,
  isParentChildReference,
  modelId,
  isComponent,
  isGroup,
  isVisualDiffMode,
  isPlaceholder,
  isReference,
}: GetLabelContainerArgs) => {
  container.setAttribute(GLOBAL_HANDLER_ID_ATTRIBUTE, modelId);
  const classNames = classes(
    isComponent
      ? 'component'
      : isGroup
        ? 'edgeGroup'
        : isParentChildReference
          ? 'parent-child-reference'
          : 'integration',
    isVisualDiffMode && 'label-visual-diff-mode',
    isPlaceholder && 'visual-diff-placeholder-label'
  );
  container.setAttribute('class', classNames);

  const entityIdAttribute = isComponent
    ? COMPONENT_ID_ATTRIBUTE
    : isReference
      ? REFERENCE_ID_ATTRIBUTE
      : null;

  if (entityIdAttribute) {
    container.setAttribute(entityIdAttribute, modelId);
  }

  container.style.pointerEvents = modelId ? '' : 'none';
  const centerTransformOrigin = `${layout.width / 2}px ${layout.height / 4}px 0px`;
  container.setAttribute('transform-origin', centerTransformOrigin);
};

const renderLabels = (container: SVGGElement, label: ILabel, hash: string) => {
  container.innerHTML = '';
  container.setAttribute(DATA_RENDER_HASH, hash);

  const graphItem: GraphItem | null = label.owner?.tag;
  if (!graphItem) return null;

  const layout = label.layout;

  const {
    modelId,
    isComponent,
    isVisualDiffMode,
    isPlaceholder,
    isReference,
    isParentChildReference,
    isGroup,
  } = getLabelMetaProperties(graphItem);

  enhanceLabelContainer({
    container,
    layout,
    isParentChildReference,
    modelId,
    isComponent,
    isReference,
    isGroup,
    isVisualDiffMode,
    isPlaceholder,
  });

  const edgeMarkersInset = getEdgeMarkersInset(
    graphItem,
    isReference,
    isParentChildReference
  );
  const labelFont =
    isReference || isParentChildReference
      ? BLOCK_DIAGRAM_EDGE_LABEL_FONT
      : BLOCK_DIAGRAM_NODE_LABEL_FONT;

  createAndAppendLabels({
    graphItem,
    container,
    layout,
    edgeMarkersInset,
    isPlaceholder,
    isTransparentized: graphItem.isTransparentized,
    mainLabelFont: labelFont,
  });
};

type LabelGeometry = {
  hash: string;
  edgeMarkersInset: number;
  labelText?: string;
  labelSize: Size;
  isSingleLine: boolean;
};
const labelGeometryCache = new WeakMap<object, LabelGeometry>();
function getLabelGeometry(label: ILabel): LabelGeometry | null {
  const item = label.owner?.tag as GraphItem | undefined;
  if (!item) return null;

  const labels = item.getItemLabels();
  if (!labels) return null;

  const hash = getMultiLabelHash(label);
  const cached = labelGeometryCache.get(item);
  if (cached && cached.hash === hash) return cached;

  const isReference = item.isReference() || isAggregatedGraphEdge(item);
  const isParentChildReference = isParentChildGraphEdge(item);
  const labelFont =
    isReference || isParentChildReference
      ? BLOCK_DIAGRAM_EDGE_LABEL_FONT
      : BLOCK_DIAGRAM_NODE_LABEL_FONT;

  const edgeMarkersInset = getEdgeMarkersInset(
    item,
    isReference,
    isParentChildReference
  );

  const labelText = labels.mainLabel;
  const labelSize = labelText
    ? measureLabelElement(labelText, labelFont)
    : Size.ZERO;

  const isSingleLine =
    !labels.legacyTruncatedFieldValueAndLabelForNode &&
    labelSize.height < 2 * labelFont.fontSize;

  const geom: LabelGeometry = {
    hash,
    edgeMarkersInset,
    labelText: labelText ?? undefined,
    labelSize,
    isSingleLine,
  };
  labelGeometryCache.set(item, geom);
  return geom;
}

class MultiLabelStyle extends LabelStyleBase {
  static Instance = new MultiLabelStyle();

  public static getStyle(
    _context: IRenderContext,
    label: ILabel
  ): LabelStyleProperties[] | null {
    const graphItem = label.owner?.tag as GraphItem | undefined;
    const itemLabels = graphItem?.getItemLabels();
    if (!graphItem || !itemLabels) return null;

    const isParentChildReference = isParentChildGraphEdge(graphItem);
    const isReference =
      graphItem.isReference() || isAggregatedGraphEdge(graphItem);
    const labelFont =
      isParentChildReference || isReference
        ? BLOCK_DIAGRAM_EDGE_LABEL_FONT
        : BLOCK_DIAGRAM_NODE_LABEL_FONT;

    const labelStyleArray = itemLabels.mainLabel
      ? [
          createLabelStyle(
            labelFont,
            { kind: 'text', text: itemLabels.mainLabel },
            {
              textAlign: 'center',
              dominantBaseline: 'middle',
              textColor: colors.black950,
              isTransparentized: graphItem.isTransparentized,
            }
          ),
        ]
      : [];

    return labelStyleArray;
  }

  public static getLayout(_context: IRenderContext, label: ILabel): Rect[] {
    const geom = getLabelGeometry(label);
    if (!geom) return [];

    const cx = label.layout.width / 2;
    const rects: Rect[] = [];

    if (geom.labelText) {
      const x = cx - geom.labelSize.width / 2;
      const y = geom.isSingleLine
        ? LABEL_VERTICAL_PADDING / 2
        : LABEL_VERTICAL_PADDING + geom.edgeMarkersInset;
      rects.push(new Rect(x, y, geom.labelSize.width, geom.labelSize.height));
    }

    return rects;
  }

  private constructor() {
    super();
  }

  protected override lookup(label: ILabel, type: Constructor): any {
    if (type === LabelExportSupport) {
      const toAbsolute = (_ctx: IRenderContext, lbl: ILabel): Rect[] => {
        const rel = MultiLabelStyle.getLayout(_ctx, lbl);
        const base = lbl.layout.bounds;
        return rel.map(
          r => new Rect(base.x + r.x, base.y + r.y, r.width, r.height)
        );
      };
      return {
        toJSON: createMultiLabelExporter(toAbsolute, MultiLabelStyle.getStyle),
      } as LabelExportSupport;
    }
    return super.lookup(label, type);
  }

  override createVisual(context: IRenderContext, label: ILabel) {
    const hash = getMultiLabelHash(label);
    const g = createSvgElement('g');
    renderLabels(g, label, hash);
    LabelStyleBase.createLayoutTransform(context, label.layout, true).applyTo(
      g
    );
    return new SvgVisual(g);
  }

  override updateVisual(
    context: IRenderContext,
    oldVisual: Visual,
    label: ILabel
  ) {
    const container = (oldVisual as SvgVisual).svgElement as SVGGElement;
    const oldHash = container.getAttribute(DATA_RENDER_HASH);
    const newHash = getMultiLabelHash(label);
    if (oldHash !== newHash) {
      renderLabels(container, label, newHash);
    }
    LabelStyleBase.createLayoutTransform(context, label.layout, true).applyTo(
      container
    );
    return oldVisual;
  }

  protected override getPreferredSize(label: ILabel): Size {
    const item = label.owner?.tag as GraphItem | undefined;
    if (!item) return Size.EMPTY;
    const labels = item.getItemLabels();
    if (!labels) return Size.EMPTY;

    const geom = getLabelGeometry(label);
    if (!geom) return Size.EMPTY;

    const edgeMarkersInsets = geom.edgeMarkersInset * 2;
    const addedLayoutWidth =
      LABEL_HORIZONTAL_PADDING +
      OTHER_LABEL_TEXT_ELEMENT_HORIZONTAL_PADDING * 2 +
      edgeMarkersInsets;

    const totalLabelHeight =
      geom.labelSize.height + LABEL_VERTICAL_PADDING + edgeMarkersInsets;

    const legacyH = labels.legacyTruncatedFieldValueAndLabelForNode
      ? LEGACY_FORMATTING_VALUE_HEIGHT_NODE
      : 0;
    const legacyW = labels.legacyTruncatedFieldValueAndLabelForNode
      ? measureNodeLabelWidth(labels.legacyTruncatedFieldValueAndLabelForNode)
      : 0;
    const height = totalLabelHeight + legacyH;
    return new Size(
      Math.max(geom.labelSize.width, legacyW) + addedLayoutWidth,
      height
    );
  }
}

export { MultiLabelStyle };
